/**
 * Two `Intl` formatters built once, at module load, rather than one per call — the cost `ago`
 * and `dateTime` used to pay on every render of every card in the Environments/Games grids.
 *
 * The relative formatter's locale is pinned to `'en'`, unlike the other two: `ago`'s output
 * feeds fixed-shape literals (`just now`, `5m ago`, `3h ago`, `2d ago`) that the rest of the UI
 * (and this module's own tests) compare byte-for-byte, so it must not silently start
 * translating on a non-English browser the way the runtime-default locale would. `dateTime` and
 * the >7-day absolute fallback stay on the default locale, as `toLocaleString()`/
 * `toLocaleDateString()` were before them.
 */
const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'always', style: 'narrow' });
const dateOnlyFormatter = new Intl.DateTimeFormat();
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
});

/**
 * Relative time, for "last pulled" / "pushed N ago". Absolute once it stops being useful as
 * relative. `now` defaults to `Date.now()`; a caller (a test) can fix it to make the bands
 * deterministic.
 */
export const ago = (iso: string | null | undefined, now: number = Date.now()): string => {
    if (!iso) return 'never';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 'never';

    const seconds = Math.round((now - then) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return relativeTimeFormatter.format(-Math.floor(seconds / 60), 'minute');
    if (seconds < 86_400) return relativeTimeFormatter.format(-Math.floor(seconds / 3600), 'hour');
    if (seconds < 7 * 86_400) return relativeTimeFormatter.format(-Math.floor(seconds / 86_400), 'day');
    return dateOnlyFormatter.format(new Date(iso));
};

export const dateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date);
};
